import { motion } from 'framer-motion';
import React, { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { SvgRecording } from '../../../icons';
import { hasPastMovieProgramPlaybackOption } from '../../../queries/ats/playback/utils';
import { Asset } from '../../../queries/ats/titles/types/asset';
import { ProgramSource, RerunProgram } from '../../../queries/ats/titles/types/sources';
import { getPreferredRecording } from '../../../queries/ats/titles/utils/recording-utils';
import { isPlayable } from '../../../queries/ats/titles/utils/sources-utils';
import { ActionButton } from '../details-button/action-button/action-button';
import { DeleteRecordingButton } from '../details-button/delete-recording-button';
import { MoreEpisodesButton } from '../details-button/more-episodes-button';
import { RecordButton } from '../details-button/record-button';
import { WatchFromArchive } from '../details-button/watch-from-archive';
import { WatchFromStartButton } from '../details-button/watch-from-start-button';
import { WatchRecordingButton } from '../details-button/watch-recording-button';
import { getProgress, getTimeStatus } from '../details-call-to-action/utils';
import { FocusedAsset } from '../use-focused-asset';
import { buttonProps } from './secondary-buttons-variants';

interface ProgramSecondaryButtonsRowProps {
  asset: Asset;
  focusedAssed: FocusedAsset;
  source: ProgramSource;
}

const MotionActionButton = motion(ActionButton);
const MotionDeleteRecordingButton = motion(DeleteRecordingButton);
const MotionWatchFromStartButton = motion(WatchFromStartButton);
const MotionWatchRecordingButton = motion(WatchRecordingButton);
const MotionWatchFromArchive = motion(WatchFromArchive);
const MotionMoreEpisodesButton = motion(MoreEpisodesButton);

export function ProgramSecondaryButtonsRow({ asset, focusedAssed, source }: ProgramSecondaryButtonsRowProps) {
  const status = getTimeStatus(source);

  switch (status) {
    case 'future':
      return <FutureProgramSecondaryButtonsRow asset={asset} focusedAssed={focusedAssed} source={source} />;
    case 'past':
      return <PastProgramSecondaryButtonsRow asset={asset} focusedAssed={focusedAssed} source={source} />;
    case 'present':
      return <PresentProgramSecondaryButtonsRow asset={asset} focusedAssed={focusedAssed} source={source} />;
  }
}

function FutureProgramSecondaryButtonsRow({ asset, source }: ProgramSecondaryButtonsRowProps) {
  const recording = getPreferredRecording(source, true);

  const rerun = source.reruns[0] as RerunProgram | undefined;

  if (rerun !== undefined && rerun.relatedSource !== undefined) {
    return (
      <Fragment>
        {asset.type === 'episode' && (
          <MotionMoreEpisodesButton {...buttonProps('more-episodes')} asset={asset} source={source} />
        )}

        {recording !== undefined && recording.status === 'completed' && (
          <MotionWatchRecordingButton {...buttonProps('watch-recording')} recording={recording} />
        )}
      </Fragment>
    );
  }

  return (
    <Fragment>
      {asset.type === 'episode' && (
        <MotionMoreEpisodesButton {...buttonProps('more-episodes')} asset={asset} source={source} />
      )}

      {recording === undefined && rerun !== undefined && (
        <MotionWatchFromArchive {...buttonProps('watch-from-archive')} rerun={rerun} />
      )}

      {recording !== undefined && recording.status !== 'completed' && rerun !== undefined && (
        <MotionWatchFromArchive {...buttonProps('watch-from-archive')} rerun={rerun} />
      )}

      {recording !== undefined && recording.status === 'completed' && (
        <MotionWatchRecordingButton {...buttonProps('watch-recording')} recording={recording} />
      )}
    </Fragment>
  );
}

function PastProgramSecondaryButtonsRow({ asset, focusedAssed, source }: ProgramSecondaryButtonsRowProps) {
  const progress = getProgress(source.bookmark, focusedAssed);
  const recording = getPreferredRecording(source, false);

  return (
    <Fragment>
      {progress !== undefined && isPlayable(source) && (
        <MotionWatchFromStartButton {...buttonProps('watch-from-start')} source={source} />
      )}

      {asset.type === 'episode' && (
        <MotionMoreEpisodesButton {...buttonProps('more-episodes')} asset={asset} source={source} />
      )}

      {source.permissions.supportsRecording && <RecordProgramButton asset={focusedAssed} source={source} />}

      {recording !== undefined && (
        <MotionDeleteRecordingButton
          {...buttonProps('delete-recording')}
          asset={asset}
          focusedAsset={focusedAssed}
          source={source}
        />
      )}
    </Fragment>
  );
}

function PresentProgramSecondaryButtonsRow({ asset, focusedAssed, source }: ProgramSecondaryButtonsRowProps) {
  return (
    <Fragment>
      {isPlayable(source) && <MotionWatchFromStartButton {...buttonProps('watch-from-start')} source={source} />}

      {asset.type === 'episode' && (
        <MotionMoreEpisodesButton {...buttonProps('more-episodes')} asset={asset} source={source} />
      )}

      {source.permissions.supportsRecording && <RecordProgramButton asset={focusedAssed} source={source} />}
    </Fragment>
  );
}

interface RecordProgramButtonProps {
  asset: Asset;
  source: ProgramSource;
}

function RecordProgramButton({ asset, source }: RecordProgramButtonProps) {
  const { t } = useTranslation();

  return (
    <RecordButton asset={asset} source={source}>
      {({ hasRecording, open }) => {
        if (asset.type === 'movie') {
          if (hasPastMovieProgramPlaybackOption(asset)) {
            return <></>;
          }
        }
        return (
          <Fragment>
            <MotionActionButton {...buttonProps('record')} variant="bordered" onClick={open}>
              <SvgRecording />
              {hasRecording ? t('details:manage-recording') : t('details:record')}
            </MotionActionButton>
          </Fragment>
        );
      }}
    </RecordButton>
  );
}
